import { useQuery } from '@tanstack/react-query'
import { Building2, MoreHorizontal } from 'lucide-react'
import { Badge, Button, Card, CardHeader, Dropdown, IconButton, ProgressBar, Table, type Column } from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatDayLabel, formatNumber } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { useDisclosure } from '@/hooks'
import { toast } from '@/stores'
import type { Tenant } from '@/types'
import { CreateTenantModal } from './components/CreateTenantModal'

const REGION_KEY: Record<Tenant['region'], string> = {
  'ap-south-1': 'tenants.region.ap',
  'eu-west-1': 'tenants.region.eu',
  'us-east-1': 'tenants.region.us',
}

const STATUS_KEY: Record<Tenant['status'], string> = {
  active: 'tenants.status.active',
  onboarding: 'tenants.status.onboarding',
  suspended: 'tenants.status.suspended',
}

/**
 * Internal, cross-tenant view. Owner role only.
 *
 * Deliberately read-mostly: it answers "which customer is near their limit" and
 * "which one is stuck in onboarding". It shows no meeting content — a support
 * tool that can read a customer's transcripts is a tool that will, and the
 * platform-admin RLS policy behind these routes covers the tenants table alone,
 * so asking for a transcript here would return nothing.
 *
 * The one write is provisioning, because onboarding a customer is a product
 * action. "onboarding" means the owner invitation has not been claimed yet.
 */
export function TenantsPage() {
  const t = useT()
  const createDialog = useDisclosure()
  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.admin.tenants,
    queryFn: () => api.get<Tenant[]>('/admin/tenants'),
  })

  const columns: Column<Tenant>[] = [
    {
      id: 'name',
      header: t('tenants.col.tenant'),
      cell: (row) => (
        <span className="min-w-0">
          <span className="block truncate font-medium text-fg">{row.name}</span>
          <span className="block truncate text-sm text-fg-subtle">
            {row.domain}
            {row.allowVerifiedDomainJoin && (
              // Everyone at this domain can sign in and land here. There is no
              // route to change it after provisioning, so this row is the only
              // place it is ever visible.
              <span className="ml-2 whitespace-nowrap text-xs text-fg-subtle">{t('tenants.openToDomain')}</span>
            )}
          </span>
        </span>
      ),
    },
    { id: 'plan', header: t('tenants.col.plan'), width: 'w-28', cell: (row) => <Badge tone="brand">{row.plan}</Badge> },
    {
      id: 'status',
      header: t('tenants.col.status'),
      width: 'w-32',
      cell: (row) => (
        <Badge tone={row.status === 'active' ? 'success' : row.status === 'onboarding' ? 'info' : 'danger'}>
          {t(STATUS_KEY[row.status])}
        </Badge>
      ),
    },
    {
      id: 'seats',
      header: t('tenants.col.seats'),
      width: 'w-24',
      hideOnMobile: true,
      cell: (row) => `${row.seats.used}/${row.seats.total}`,
    },
    {
      id: 'usage',
      header: t('tenants.col.minutes'),
      width: 'w-44',
      hideOnMobile: true,
      cell: (row) => (
        <span className="block">
          <span className="mb-1 block text-sm text-fg-muted">
            {formatNumber(row.minutesUsed)} / {formatNumber(row.minutesIncluded)}
          </span>
          <ProgressBar
            value={row.minutesUsed / row.minutesIncluded}
            tone={row.minutesUsed / row.minutesIncluded > 0.85 ? 'warning' : 'brand'}
          />
        </span>
      ),
    },
    {
      id: 'region',
      header: t('tenants.col.region'),
      width: 'w-24',
      hideOnMobile: true,
      cell: (row) => t(REGION_KEY[row.region]),
    },
    {
      id: 'prefix',
      header: t('tenants.col.storagePrefix'),
      width: 'w-40',
      hideOnMobile: true,
      // Immutable once allocated and present in every S3 key this tenant ever
      // produces, so this table is the only place a wrong one gets noticed.
      cell: (row) => (
        <span className="font-mono text-xs text-fg-subtle">{row.storagePrefix ?? '—'}</span>
      ),
    },
    {
      id: 'created',
      header: t('tenants.col.created'),
      width: 'w-32',
      hideOnMobile: true,
      cell: (row) => <span className="text-fg-muted">{formatDayLabel(row.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: 'w-12',
      cell: (row) => (
        <Dropdown
          trigger={({ toggle }) => (
            <IconButton label={t('tenants.manage', { name: row.name })} icon={<MoreHorizontal />} size="sm" onClick={toggle} />
          )}
          items={[
            { id: 'impersonate', label: t('tenants.openSupportSession'), onSelect: () => toast.warning(t('tenants.toast.supportAudited')) },
            { id: 'limits', label: t('tenants.adjustLimits'), onSelect: () => toast.info(t('tenants.toast.notBuilt')) },
            {
              id: 'suspend',
              label: row.status === 'suspended' ? t('tenants.reactivate') : t('tenants.suspendTenant'),
              destructive: row.status !== 'suspended',
              separatorBefore: true,
              onSelect: () => toast.warning(t('tenants.toast.secondApprover')),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title={t('tenants.title')}
        description={t('tenants.description')}
        actions={
          <Button
            variant="primary"
            leftIcon={<Building2 className="h-4 w-4" />}
            onClick={createDialog.open}
          >
            {t('tenants.createTenant')}
          </Button>
        }
      />
      <Card>
        <CardHeader title={t('tenants.count', { count: data?.length ?? 0 })} />
        <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
          <Table columns={columns} rows={data ?? []} rowKey={(row) => row.id} />
        </QueryBoundary>
      </Card>
      <CreateTenantModal isOpen={createDialog.isOpen} onClose={createDialog.close} />
    </div>
  )
}
